import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { catchError, mergeMap, Observable, of, Subject } from 'rxjs';
import { CollaborationService } from '../services/collaboration.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private tokenString = '';
  private helper = new JwtHelperService();
  private tokenSubject: Subject<any> | null = null; // Subject for sharing the token request.

  constructor(private collaborationService: CollaborationService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const skipInercept = req.headers.has('skip');

    if (skipInercept) {
      const request = req.clone({
        headers: req.headers.delete('skip'), // Removing the skip header
      });
      return next.handle(request);
    } else {
      let token = localStorage.getItem('collaborationToken') || this.collaborationService.apimToken;

      if (token && !this.helper.isTokenExpired(token)) {
        this.tokenString = token;
        return this.handleRequest(req, next);
      } else {
        if (!this.tokenSubject) {
          this.tokenSubject = new Subject();
          this.collaborationService.getAuthenticationTokenFromAPIM().pipe(
            catchError((err) => {
              this.collaborationService.displayErrorMessage(err, "Error while getting token from APIM. Please try after some time");
              return of(null);
            }),
          ).subscribe((newToken: any) => {
            if (newToken) {
              this.collaborationService.apimToken = newToken.access_token;
              this.tokenString = newToken.access_token;
            }

            this.tokenSubject?.next(this.tokenString);
            this.tokenSubject?.complete();
            this.tokenSubject = null;
          });
        }

        // While the token is being fetched, wait for it.
        return this.tokenSubject.pipe(
          mergeMap(token => {
            if (token) {
              // After the new token is fetched, retry the original request
              return this.handleRequest(req, next);
            } else {
              // If there's an error getting the token, just proceed without token.
              return next.handle(req);
            }
          })
        );
      }
    }
  }

  private handleRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let request = req.clone({
      headers: req.headers
        .set('Content-Type', 'application/json')
        .set('Authorization', `Bearer ${this.tokenString}`)
        .set('apiVersion', '1'),
    });
    return next.handle(request); // Proceed with the next handler
  }
}
